import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { useParams } from '@tanstack/react-router';
import { memo, Suspense } from 'react';

import { BranchNames } from '@/components/branch-names';
import { EditableText } from '@/components/editable-text';
import { DescriptionEditor } from '@/components/editor/description-editor';
import { PullStateIcon } from '@/components/ui/pull-state-icon';
import { Skeleton } from '@/components/ui/skeleton';
import { LabelManager } from '@/features/pulls/components/label-manager';
import { useZero } from '@/lib/zero';

export const PullDescription = memo(() => {
  return (
    <Suspense fallback={<Skeleton className="w-full rounded-md h-32" />}>
      <PullDescriptionInner />
    </Suspense>
  );
});

const PullDescriptionInner = memo(() => {
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });
  const z = useZero();

  const [pull] = useZeroQuery(
    z.query.PullsTable.where(({ and, cmp }) => and(cmp('owner', owner), cmp('repo', repo), cmp('number', number))).one()
  );

  if (!pull) {
    return <Skeleton className="w-full rounded-md h-32" />;
  }

  // pull is now available
  const handleTitleChange = async (newTitle: string) => {
    z.mutate.PullsTable.update({
      nodeId: pull.nodeId,
      title: newTitle,
    });
  };

  return (
    <div className="w-full py-6 flex" data-section-name="Description">
      <div className="flex flex-col px-4 flex-4 min-w-xl">
        <div className="flex items-start gap-6 justify-between">
          <div className="flex items-center gap-3" data-main-title>
            <PullStateIcon state={pull.state} width={22} height={22} />

            {/* <h1 className="text-2xl font-medium">{pull.title}</h1> */}
            <EditableText className="text-2xl font-medium" initialValue={pull.title} onSubmit={handleTitleChange} />
          </div>
          <BranchNames
            owner={owner}
            repo={repo}
            pull_number={number}
            author={{
              login: pull.author.login,
              avatar_url: pull.author.avatarUrl,
            }}
            base={pull.base.ref}
            head={pull.head.ref}
          />
        </div>

        {/* Labels section */}
        <div className="mt-4">
          <Suspense fallback={<Skeleton className="h-6 w-32" />}>
            <LabelManager />
          </Suspense>
        </div>

        <div>
          <DescriptionEditor
            description={pull.body}
            updateDescription={({ body }) =>
              z.mutate.PullsTable.update({
                nodeId: pull.nodeId,
                owner: pull.owner,
                repo: pull.repo,
                number: pull.number,
                title: pull.title,
                body: body,
              })
            }
            className="mt-6"
          />
        </div>
      </div>
    </div>
  );
});
